import React, { useMemo } from "react"
import clsx from "clsx"
import { Grid, ButtonBase } from "@material-ui/core"

import { ActionDialog, Portal } from "@/components"
import { PopConfirmProps } from "@/components/PopConfirm"

import { getValidInteractionProps } from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/utils/nativeEvent"

import useGlobalItemSelection from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/hooks/useGlobalItemSelection"
import useConstantId from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/hooks/useConstantId"
import useStyles from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/BlockBodySectionEditor/styles"

import { FLOW_EDITOR_CONTAINER_ID } from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/config/flowResources"

export type AsideDrawerEvent = {
	closable: boolean
	/**
	 * AsideDrawer automatically closes when save button
	 * is pressed. In case you want to prevent that action, call this method.
	 */
	preventDrawerClose: () => void
}

type BlockBodySectionEditorProps = {
	title: string
	hideChildSelection?: boolean
	hideCancelButton?: boolean
	disabled?: boolean
	cancelText?: string
	saveText?: string
	ActionDialogProps?: {
		children?: React.ReactElement
		width?: string
	}
	DeletePopConfirmProps?: Omit<PopConfirmProps, "onConfirm">
	onClose?: () => void
	onOpen?: () => void
	onSave?: (event: AsideDrawerEvent) => void
}

const BlockBodySectionDialogEditor: React.FC<BlockBodySectionEditorProps> = (props) => {
	const {
		title,
		children,
		ActionDialogProps,
		onClose,
		onSave,
		onOpen,
		cancelText,
		saveText,
		disabled,
		hideChildSelection,
		hideCancelButton
	} = props

	const blockSectionId = useConstantId()
	const flowBlockSectionEditorSelection = useGlobalItemSelection("flow-editor-right-drawer")
	const classes = useStyles()

	const isBeingEdited = flowBlockSectionEditorSelection.isSelected(blockSectionId)
	const flowEditorContainer = useMemo(() => document.getElementById(FLOW_EDITOR_CONTAINER_ID), []) as Element

	const handleOpen = () => {
		flowBlockSectionEditorSelection.clearSelection()
		flowBlockSectionEditorSelection.toggleSelected(blockSectionId)

		onOpen?.()
	}

	const handleClose = () => {
		flowBlockSectionEditorSelection.clearSelection()

		onClose?.()
	}

	const buildAsideDrawerEvent = (): AsideDrawerEvent => ({
		closable: true,
		preventDrawerClose: function () {
			this.closable = false
		}
	})

	const handleSave = () => {
		const asideDrawerEvent = buildAsideDrawerEvent()

		onSave?.(asideDrawerEvent)

		if (asideDrawerEvent.closable) {
			handleClose()
		}
	}

	return (
		<>
			<Grid
				item
				component={ButtonBase}
				disabled={disabled}
				className={clsx({
					[classes.section]: true,
					[classes.sectionBeingEdited]: isBeingEdited && !hideChildSelection
				})}
				{...getValidInteractionProps({
					blackListedElements: {
						htmlTagNames: ["a"]
					},
					onClick: handleOpen
				})}
			>
				{children}
			</Grid>

			<Portal
				container={flowEditorContainer}
			>
				<ActionDialog
					openDialog={isBeingEdited}
					title={title}
					onSave={handleSave}
					onClose={handleClose}
					saveText={saveText || "SALVAR"}
					cancelText={cancelText || "CANCELAR"}
					hideCloseButton={hideCancelButton}
					maxWidth="md"
					fullWidth
				>
					<Grid
						container
						direction="column"
						justifyContent="space-between"
					>
						<Grid>
							{ActionDialogProps?.children}
						</Grid>
					</Grid>
				</ActionDialog>
			</Portal>
		</>
	)
}

export default BlockBodySectionDialogEditor
